import { useLocation } from '@react-router';
import { useCatalogDepartments } from '@apps/registry/common/components/Catalog/hooks/useCatalogDepartments/useCatalogDepartments';
import { getIsCashfundType } from '@apps/registry/common/components/Catalog/state/Catalog/Catalog.utils';
import { INTERNATIONAL_ADD_GIFTS_EMBED } from '@apps/registry/common/components/Catalog/state/CatalogEmbed/catalogEmbed.config';
import { isPublicPathWithinEmbedScope } from '@apps/registry/common/components/Catalog/state/CatalogEmbed/catalogEmbedPaths';

const CATALOG_CATEGORY_PREFIX = '/shop/catalog/';

export type IsIntlSupportedCatalogPathReturn = Readonly<{
  isResolved: boolean;
  isIntlSupported: boolean;
}>;

export const useIsIntlSupportedCatalogPath = (): IsIntlSupportedCatalogPathReturn => {
  const { pathname } = useLocation();
  const { catalogCategoryDictionary, hasLoadedOnce } = useCatalogDepartments();

  const publicPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  if (!publicPath.startsWith(CATALOG_CATEGORY_PREFIX)) {
    return { isResolved: true, isIntlSupported: false };
  }

  if (!hasLoadedOnce) {
    return { isResolved: false, isIntlSupported: false };
  }

  const entry = catalogCategoryDictionary?.[publicPath];
  const lineage = (entry?.lineageCsp ?? []).flatMap(node => (node?.publicUrlPath ? [{ publicUrlPath: node.publicUrlPath }] : []));

  // Two rules, not one literal: the whole cash funds tree is money rather than shipped goods, and
  // the intl Add Gifts embed's allowed roots add charity on top of the wedding cash funds subtree.
  const isIntlSupported =
    getIsCashfundType(entry?.publicUrlPath, lineage) ||
    isPublicPathWithinEmbedScope(INTERNATIONAL_ADD_GIFTS_EMBED, lineage.map(node => node.publicUrlPath).concat(entry ? [entry.publicUrlPath] : []));

  return { isResolved: true, isIntlSupported };
};
